import {
  Component, EventEmitter, Input, Output,
  OnInit, OnChanges, SimpleChanges, ChangeDetectorRef
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { SharedModule } from 'src/app/private/shared/shared.module';
import { AddonService } from 'src/app/private/services/addon.service';
import { InputNumberModule } from 'primeng/inputnumber';

export interface AddonFeature {
  key: string;
  label?: string;
  enabled: boolean;
  isLimit: boolean;
  value?: number;
  limit?: number;
  unit_type?: string;  // Unit type from feature definition
}

@Component({
  selector: 'app-addon-form',
  standalone: true,
  imports: [SharedModule, InputNumberModule],
  templateUrl: './addon-form.component.html',
  styleUrl: './addon-form.component.scss'
})
export class AddonFormComponent implements OnInit, OnChanges {

  @Input() addonData: any = null;
  @Input() visible: boolean = false;

  // ⬇ Receive same features list that package form receives
  @Input() featuresList: any = {};  // { fixed:[], limit:[] }

  @Output() close = new EventEmitter<void>();
  @Output() reload = new EventEmitter<void>();

  addonForm!: FormGroup;
  isLoading = false;
  showJson = false;

  /** Processed feature lists */
  fixedFeatureOptions: any[] = [];
  limitFeatureOptions: any[] = [];

  /** Internal structure storing both feature types */
  addonFeatures: AddonFeature[] = [];

  constructor(
    private fb: FormBuilder,
    private addonService: AddonService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef
  ) {
     
  }

  getLimitLabel(key: string): string {
    return this.limitFeatureOptions.find(x => x.feature_key === key)?.label || this.formatKey(key);
  }

  getFeatureLabel(key: string): string {
    return this.fixedFeatureOptions.find(x => x.feature_key === key)?.label || this.formatKey(key);
  }

  // optional pretty formatter
  formatKey(str: string): string {
    if (!str) return '';
    return str.replace(/_/g, ' ')
              .toLowerCase()
              .replace(/\b\w/g, c => c.toUpperCase());
  }


  ngOnInit() {
    this.initForm();
    this.processFeatureOptions();
  }

  ngOnChanges(changes: SimpleChanges) {
    // When visible changes to true, wait a bit for the dialog to render
    if (changes['visible'] && changes['visible'].currentValue === true) {
      console.log('👁️ Dialog became visible');
      // Give the dialog time to render
      setTimeout(() => {
        if (this.addonData) {
          console.log('🔄 Re-populating form after dialog opened');
          this.populateForm();
        }
      }, 100);
    }

    // When addonData changes (e.g., when opening edit dialog)
    if (changes['addonData'] && changes['addonData'].currentValue) {
      console.log('📦 Addon data changed:', changes['addonData'].currentValue);
      
      // Make sure form is initialized
      if (!this.addonForm) {
        this.initForm();
      }
      
      // Make sure feature options are processed first
      if (this.limitFeatureOptions.length === 0 && this.fixedFeatureOptions.length === 0) {
        this.processFeatureOptions();
      }
      
      // Populate after a small delay to ensure everything is ready
      setTimeout(() => {
        this.populateForm();
      }, 50);
    } else if (changes['addonData'] && !changes['addonData'].currentValue) {
      // When creating new addon
      console.log('➕ Creating new addon');
      this.resetToDefaults();
    }

    // When featuresList changes
    if (changes['featuresList'] && changes['featuresList'].currentValue) {
      console.log('📋 Features list changed');
      this.processFeatureOptions();
      // Re-populate if we have addon data
      if (this.addonData && this.addonForm) {
        setTimeout(() => {
          this.populateForm();
        }, 50);
      }
    }
  }

  initForm() {
    this.addonForm = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      price: [0, [Validators.required, Validators.min(0)]],
      is_active: [true, Validators.required]
    });
  }

  /** Separate fixed + limit features for dropdown options - ONLY PARENT FEATURES */
  processFeatureOptions() {
    // Filter only parent features (modules - features without parent_feature_key)
    this.fixedFeatureOptions = (this.featuresList?.fixed || []).filter(
      (feature: any) => !feature.parent_feature_key
    );
    
    this.limitFeatureOptions = (this.featuresList?.limit || []).filter(
      (feature: any) => !feature.parent_feature_key
    );
  }

  /** Default fields for new addon */
  resetToDefaults(): void {
    this.addonFeatures = [];
  }

  populateForm() {
    if (!this.addonData) {
      console.log('⚠️ No addon data to populate');
      return;
    }

    if (!this.addonForm) {
      console.log('⚠️ Form not initialized, initializing now...');
      this.initForm();
    }

    console.log('📝 Populating addon form with data:', this.addonData);
    console.log('📋 Available limit options:', this.limitFeatureOptions);
    console.log('📋 Available fixed options:', this.fixedFeatureOptions);
    console.log('🔍 Has features property?', 'features' in this.addonData);
    console.log('🔍 Has feature property?', 'feature' in this.addonData);
    console.log('🔍 features value:', this.addonData.features);
    console.log('🔍 feature value:', this.addonData.feature);

    const formValues = {
      name: this.addonData.name,
      description: this.addonData.description,
      price: this.addonData.price || 0,
      is_active: this.addonData.is_active !== false
    };

    console.log('📝 Patching form with values:', formValues);
    this.addonForm.patchValue(formValues);
    console.log('✅ Form patched. Current form value:', this.addonForm.value);

    // Parse features from API response
    if (this.addonData.feature && typeof this.addonData.feature === 'object') {
      // Check if feature has limits/fixed arrays (new format)
      if (this.addonData.feature.limits || this.addonData.feature.fixed) {
        console.log('✅ Using new format: feature.limits/feature.fixed');
        const limits = this.addonData.feature.limits || [];
        const fixed = this.addonData.feature.fixed || [];
        
        this.addonFeatures = [...limits, ...fixed];
      } else {
        // Old format: feature is a flat object { key: { value: ... } or { enabled: ... } }
        console.log('✅ Using old format: flat feature object');
        const limits: AddonFeature[] = [];
        const fixed: AddonFeature[] = [];

        Object.keys(this.addonData.feature).forEach(k => {
          const val = this.addonData.feature[k];
          
          // Check if this is a limit feature (has value property)
          const isLimitFeature = this.limitFeatureOptions.some(opt => opt.feature_key === k);
          const isFixedFeature = this.fixedFeatureOptions.some(opt => opt.feature_key === k);

          if (isLimitFeature) {
            let value = 0;
            let unit_type = '';
            
            if (typeof val === 'object' && val !== null) {
              value = val.value || 0;
              unit_type = val.unit_type || '';
            } else {
              value = Number(val) || 0;
            }
            
            // Get unit_type from feature definition if not in data
            const featureDef = this.limitFeatureOptions.find(opt => opt.feature_key === k);
            if (!unit_type && featureDef) {
              unit_type = featureDef.unit_type || '';
            }

            limits.push({
              key: k,
              label: this.getLimitLabel(k),
              enabled: true,
              isLimit: true,
              value: value,
              unit_type: unit_type
            });
          } else if (isFixedFeature) {
            const enabled = typeof val === 'boolean' ? val : (val?.enabled !== false);
            fixed.push({
              key: k,
              label: this.getFeatureLabel(k),
              enabled: enabled,
              isLimit: false
            });
          }
        });

        this.addonFeatures = [...limits, ...fixed];
      }
    }

    console.log('✅ Final addon features:', this.addonFeatures);
  }

  /** Get available limit options excluding already added ones */
  getAvailableLimitOptions(currentLimit?: AddonFeature): any[] {
    const addedKeys = this.addonFeatures
      .filter(f => f.isLimit && f !== currentLimit)
      .map(f => f.key);
    
    return this.limitFeatureOptions.filter(
      option => !addedKeys.includes(option.feature_key)
    );
  }

  /** Get available feature options excluding already added ones */
  getAvailableFeatureOptions(currentFeature?: AddonFeature): any[] {
    const addedKeys = this.addonFeatures
      .filter(f => !f.isLimit && f !== currentFeature)
      .map(f => f.key);
    
    return this.fixedFeatureOptions.filter(
      option => !addedKeys.includes(option.feature_key)
    );
  }

  /** LIMITS SECTION */
  addLimit() {
    if (!this.limitFeatureOptions.length) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Credits Available',
        detail: 'No credits options are available to add'
      });
      return;
    }

    const addedLimitKeys = this.addonFeatures
      .filter(f => f.isLimit)
      .map(f => f.key);

    const availableLimit = this.limitFeatureOptions.find(
      option => !addedLimitKeys.includes(option.feature_key)
    );

    if (!availableLimit) {
      this.messageService.add({
        severity: 'info',
        summary: 'All Credits Added',
        detail: 'All available credits have already been added to this add-on'
      });
      return;
    }

    this.addonFeatures.push({
      key: availableLimit.feature_key,
      label: availableLimit.label,
      enabled: true,
      isLimit: true,
      value: 0,
      unit_type: availableLimit.unit_type  // Get unit_type from feature definition
    });

    this.cdr.detectChanges();
  }

  /** FEATURES SECTION */
  addFeature() {
    if (!this.fixedFeatureOptions.length) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Features Available',
        detail: 'No feature options are available to add'
      });
      return;
    }

    const addedFeatureKeys = this.addonFeatures
      .filter(f => !f.isLimit)
      .map(f => f.key);

    const availableFeature = this.fixedFeatureOptions.find(
      option => !addedFeatureKeys.includes(option.feature_key)
    );

    if (!availableFeature) {
      this.messageService.add({
        severity: 'info',
        summary: 'All Features Added',
        detail: 'All available features have already been added to this add-on'
      });
      return;
    }

    this.addonFeatures.push({
      key: availableFeature.feature_key,
      label: availableFeature.label,
      enabled: true,
      isLimit: false
    });

    this.cdr.detectChanges();
  }

  removeFeature(item: AddonFeature) {
    this.addonFeatures = this.addonFeatures.filter(f => f !== item);
  }

  onLimitChange(event: any, limit: AddonFeature) {
    if (!event.value) {
      limit.label = '';
      limit.unit_type = '';
      return;
    }

    const selectedLimit = this.limitFeatureOptions.find(f => f.feature_key === event.value);
    if (selectedLimit) {
      limit.label = selectedLimit.label;
      limit.unit_type = selectedLimit.unit_type;  // Update unit_type from feature definition
    }
  }

  onFeatureChange(event: any, feature: AddonFeature) {
    if (!event.value) {
      feature.label = '';
      return;
    }

    const selectedFeature = this.fixedFeatureOptions.find(f => f.feature_key === event.value);
    if (selectedFeature) {
      feature.label = selectedFeature.label;
    }
  }

  /** Filters */
  getLimits() {
    return this.addonFeatures.filter(f => f.isLimit);
  }

  getFeatures() {
    return this.addonFeatures.filter(f => !f.isLimit);
  }

  /** Convert to API format - matching package structure */
  buildFeatureObject(): { limits: any[], fixed: any[] } {
    return {
      limits: this.getLimits(),
      fixed: this.getFeatures()
    };
  }

  submitForm() {
    if (this.addonForm.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fill all required fields'
      });
      return;
    }

    const payload = {
      name: this.addonForm.value.name,
      description: this.addonForm.value.description,
      is_active: this.addonForm.value.is_active,
      price: this.addonForm.value.price,
      feature: this.buildFeatureObject()
    };

    this.isLoading = true;

    if (this.addonData?.id) {
      this.updateAddon({ ...payload, id: this.addonData.id });
    } else {
      this.createAddon(payload);
    }
  }

  createAddon(payload: any) {
    this.addonService.createPackageAddon(payload).subscribe({
      next: () => {
        this.success("Add-on created successfully");
      },
      error: () => this.error()
    });
  }

  updateAddon(payload: any) {
    this.addonService.updatePackageAddon(payload).subscribe({
      next: () => {
        this.success("Add-on updated successfully");
      },
      error: () => this.error()
    });
  }

  success(msg: string) {
    this.isLoading = false;
    this.messageService.add({ severity: 'success', summary: 'Success', detail: msg });
    this.reload.emit();
    this.handleClose();
  }

  error() {
    this.isLoading = false;
    this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Something went wrong' });
  }

  handleClose() {
    this.addonForm.reset();
    this.addonFeatures = [];
    this.showJson = false;
    this.close.emit();
  }

  getJsonPreview() {
    return {
      name: this.addonForm.value.name,
      description: this.addonForm.value.description,
      price: this.addonForm.value.price,
      is_active: this.addonForm.value.is_active,
      features: this.buildFeatureObject()
    };
  }
}
